import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const MEPTools: React.FC = () => {
  return (
    <>
      {/* Page Header */}
      <section className="bg-dark text-white py-5 mt-5">
        <div className="container py-4">
          <div className="row align-items-center">
            <div className="col-lg-8">
              <h1 className="display-5 fw-bold mb-3">MEP <span className="text-primary">Tools</span></h1>
              <p className="lead mb-0">A cloud-native SaaS platform for MEP field professionals and facility managers. Precision calculations, condition assessments, and energy analysis — from any device, on any job site.</p>
            </div>
            <div className="col-lg-4 text-lg-end mt-4 mt-lg-0">
              <Link to="/contact" className="btn btn-primary btn-lg">
                Request a Demo <i className="bi bi-arrow-right ms-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="section section-light">
        <div className="container">
          <div className="row g-5 align-items-center">
            <div className="col-lg-6" data-aos="fade-right">
              <h2 className="mb-4">Built by MEP practitioners, <span className="text-primary">for MEP practitioners</span></h2>
              <p className="lead mb-4">Most CMMS and field service platforms are designed for general facility management. MEP Tools is different — it was built specifically for the calculations, assessments, and documentation that mechanical, electrical, and plumbing professionals actually do in the field.</p>
              <p className="mb-4">Whether you're testing duct leakage on a new construction job, evaluating boiler efficiency in an aging facility, or delivering a formal facility condition report to an owner, MEP Tools gives you the right tool for the job — in the cloud, on your phone, with no paper and no re-entry.</p>
              <div className="row g-3">
                <div className="col-6">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-phone-fill text-primary me-2 fs-5"></i>
                    <span>Mobile-first design</span>
                  </div>
                </div>
                <div className="col-6">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-cloud-fill text-primary me-2 fs-5"></i>
                    <span>Cloud-native, no installs</span>
                  </div>
                </div>
                <div className="col-6">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-building-fill text-primary me-2 fs-5"></i>
                    <span>Per-building pricing</span>
                  </div>
                </div>
                <div className="col-6">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-people-fill text-primary me-2 fs-5"></i>
                    <span>Multi-user, multi-project</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6" data-aos="fade-left">
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80"
                alt="MEP Tools dashboard on mobile"
                className="img-fluid rounded-4 shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Current Modules */}
      <section className="section bg-eco-pattern">
        <div className="container">
          <div className="row text-center mb-5">
            <div className="col-lg-8 mx-auto">
              <h2 className="mb-3">Current <span className="text-primary">Modules</span></h2>
              <p className="lead">Six precision tools covering the most critical MEP field workflows — available today in your subscription.</p>
            </div>
          </div>

          <div className="row g-4">
            {/* DALT */}
            <div className="col-md-6 col-lg-4" data-aos="fade-up">
              <div className="card h-100 p-4">
                <div className="feature-icon mb-3">
                  <i className="bi bi-wind"></i>
                </div>
                <h4>Duct Air Leakage Test (DALT)</h4>
                <p>Full SMACNA-compliant duct leakage calculations in the field. Enter test data and instantly get leakage class, allowable leakage, and pass/fail results per SMACNA HVAC Air Duct Leakage Test Manual.</p>
                <ul className="list-unstyled mt-3 mb-0">
                  <li className="mb-2 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>SMACNA leakage class calculations</span>
                  </li>
                  <li className="mb-2 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>Allowable vs. measured leakage comparison</span>
                  </li>
                  <li className="d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>Exportable test results per project</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Ebtron */}
            <div className="col-md-6 col-lg-4" data-aos="fade-up" data-aos-delay="100">
              <div className="card h-100 p-4">
                <div className="feature-icon mb-3">
                  <i className="bi bi-speedometer2"></i>
                </div>
                <h4>Ebtron Airflow Calibration</h4>
                <p>Precision airflow measurement and calibration tool for Ebtron flow stations and thermal dispersion sensors. Eliminates manual calculation and reduces documentation time in the field.</p>
                <ul className="list-unstyled mt-3 mb-0">
                  <li className="mb-2 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>Ebtron sensor calibration verification</span>
                  </li>
                  <li className="mb-2 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>Airflow measurement recording</span>
                  </li>
                  <li className="d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>Per-system documentation</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Power Calculator */}
            <div className="col-md-6 col-lg-4" data-aos="fade-up" data-aos-delay="200">
              <div className="card h-100 p-4">
                <div className="feature-icon mb-3">
                  <i className="bi bi-lightning-charge-fill"></i>
                </div>
                <h4>Three-Phase Power Calculator</h4>
                <p>Electrical load calculations for MEP field work. Quickly compute power, current, voltage, and power factor for three-phase systems without a separate app or spreadsheet.</p>
                <ul className="list-unstyled mt-3 mb-0">
                  <li className="mb-2 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>Three-phase KW, KVA, KVAR calculations</span>
                  </li>
                  <li className="mb-2 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>Current and voltage calculations</span>
                  </li>
                  <li className="d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>Power factor analysis</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Boiler ECM */}
            <div className="col-md-6 col-lg-4" data-aos="fade-up" data-aos-delay="300">
              <div className="card h-100 p-4">
                <div className="feature-icon mb-3">
                  <i className="bi bi-fire"></i>
                </div>
                <h4>Boiler ECM Calculator</h4>
                <p>Energy conservation measure analysis for boiler systems. Model fuel savings across five fuel types and four ECMs, with full ROI calculations to support capital planning recommendations.</p>
                <ul className="list-unstyled mt-3 mb-0">
                  <li className="mb-2 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>5 fuel types (natural gas, oil, propane, steam, electric)</span>
                  </li>
                  <li className="mb-2 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>4 ECM types with energy savings modeling</span>
                  </li>
                  <li className="d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>ROI and simple payback period calculation</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Facility Assessment */}
            <div className="col-md-6 col-lg-4" data-aos="fade-up" data-aos-delay="400">
              <div className="card h-100 p-4 border border-primary border-2">
                <div className="feature-icon mb-3">
                  <i className="bi bi-building-check"></i>
                </div>
                <h4>Facility Condition Assessment</h4>
                <span className="badge bg-primary mb-3">Core Module</span>
                <p>Weighted condition scoring system for MEP assets and building systems. Document equipment condition, generate prioritized action items, and produce a scored building-level rating for capital planning.</p>
                <ul className="list-unstyled mt-3 mb-0">
                  <li className="mb-2 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>Weighted scoring across all MEP systems</span>
                  </li>
                  <li className="mb-2 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>Building-level condition rating</span>
                  </li>
                  <li className="mb-2 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>Prioritized repair and replacement recommendations</span>
                  </li>
                  <li className="d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>Exportable assessment report</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* PM Scheduler */}
            <div className="col-md-6 col-lg-4" data-aos="fade-up" data-aos-delay="500">
              <div className="card h-100 p-4 border border-success border-2">
                <div className="feature-icon mb-3">
                  <i className="bi bi-calendar2-check"></i>
                </div>
                <h4>PM Scheduler</h4>
                <span className="badge bg-success mb-3">New</span>
                <p>Preventive maintenance scheduling and reactive work order management built for facility teams. Tied directly to your asset inventory — with a compliance calendar, dashboard, and calendar view.</p>
                <ul className="list-unstyled mt-3 mb-0">
                  <li className="mb-2 d-flex">
                    <i className="bi bi-check-circle-fill text-success me-2 mt-1"></i>
                    <span>27-task MEP PM library (HVAC, electrical, plumbing, fire &amp; life safety)</span>
                  </li>
                  <li className="mb-2 d-flex">
                    <i className="bi bi-check-circle-fill text-success me-2 mt-1"></i>
                    <span>Overdue / due-soon alerts with calendar view</span>
                  </li>
                  <li className="mb-2 d-flex">
                    <i className="bi bi-check-circle-fill text-success me-2 mt-1"></i>
                    <span>Reactive work orders with P1–P4 priority</span>
                  </li>
                  <li className="d-flex">
                    <i className="bi bi-check-circle-fill text-success me-2 mt-1"></i>
                    <span>Compliance calendar for code-required inspections</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="section section-light">
        <div className="container">
          <div className="row text-center mb-5">
            <div className="col-lg-8 mx-auto">
              <h2 className="mb-3">Who <span className="text-primary">MEP Tools</span> Is For</h2>
              <p className="lead">Designed for the people who actually do the work — and the people responsible for the buildings they work in.</p>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-md-6 col-lg-4" data-aos="fade-up">
              <div className="card h-100 p-4 text-center">
                <div className="feature-icon mx-auto mb-3">
                  <i className="bi bi-building-gear"></i>
                </div>
                <h4>Facilities Directors</h4>
                <p>Get a scored, documented picture of every building's MEP condition. Make capital planning decisions with real data, produce audit-ready reports, and stop managing by spreadsheet.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4" data-aos="fade-up" data-aos-delay="100">
              <div className="card h-100 p-4 text-center">
                <div className="feature-icon mx-auto mb-3">
                  <i className="bi bi-wrench-adjustable-circle"></i>
                </div>
                <h4>MEP Field Technicians</h4>
                <p>Run DALT, airflow, power, and boiler calculations from your phone — no paper, no manual re-entry, no hunting for the right spreadsheet. Results go straight into the project record.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4" data-aos="fade-up" data-aos-delay="200">
              <div className="card h-100 p-4 text-center">
                <div className="feature-icon mx-auto mb-3">
                  <i className="bi bi-clipboard2-data"></i>
                </div>
                <h4>Assessment & Commissioning Teams</h4>
                <p>Run condition assessments, track deficiencies, and produce structured reports — whether you're diagnosing an existing building or documenting commissioning activities through closeout.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Competitive Differentiators */}
      <section className="section bg-dark text-white">
        <div className="container">
          <div className="row text-center mb-5">
            <div className="col-lg-8 mx-auto">
              <h2 className="mb-3">What Sets MEP Tools <span className="text-primary">Apart</span></h2>
              <p className="lead">No other platform combines MEP-specific field calculations, facility condition assessment, and energy conservation analysis in a single cloud subscription.</p>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-md-6" data-aos="fade-up">
              <div className="d-flex">
                <div className="me-4 flex-shrink-0">
                  <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                    <i className="bi bi-x-circle-fill fs-5"></i>
                  </div>
                </div>
                <div>
                  <h5>Generic CMMS platforms (UpKeep, Limble, eMaint)</h5>
                  <p className="text-muted mb-0">Great for work orders and PM scheduling. Zero support for DALT, Ebtron calibration, boiler ECM analysis, or MEP-specific condition scoring.</p>
                </div>
              </div>
            </div>

            <div className="col-md-6" data-aos="fade-up" data-aos-delay="100">
              <div className="d-flex">
                <div className="me-4 flex-shrink-0">
                  <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                    <i className="bi bi-x-circle-fill fs-5"></i>
                  </div>
                </div>
                <div>
                  <h5>Proof CxTAB</h5>
                  <p className="text-muted mb-0">The closest competitor for commissioning forms. Windows desktop only — no cloud sync, no mobile-native entry, no MEP field calculations.</p>
                </div>
              </div>
            </div>

            <div className="col-md-6" data-aos="fade-up" data-aos-delay="200">
              <div className="d-flex">
                <div className="me-4 flex-shrink-0">
                  <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                    <i className="bi bi-x-circle-fill fs-5"></i>
                  </div>
                </div>
                <div>
                  <h5>Enterprise BIM platforms (Autodesk ACC)</h5>
                  <p className="text-muted mb-0">Powerful but locked to large-team BIM workflows and enterprise pricing. Not built for MEP field O&M work or small-to-mid-size facility portfolios.</p>
                </div>
              </div>
            </div>

            <div className="col-md-6" data-aos="fade-up" data-aos-delay="300">
              <div className="d-flex">
                <div className="me-4 flex-shrink-0">
                  <div className="bg-success rounded-circle text-white d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                    <i className="bi bi-check-circle-fill fs-5"></i>
                  </div>
                </div>
                <div>
                  <h5>MEP Tools</h5>
                  <p className="text-muted mb-0">The only SaaS platform combining MEP precision field calculations + facility condition assessment + energy ECM analysis. Cloud-native, mobile-first, per-building pricing.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & DBH */}
      <section id="digital-building-handoff" className="section bg-dark text-white">
        <div className="container">
          <div className="row text-center mb-5">
            <div className="col-lg-8 mx-auto">
              <h2 className="mb-3">The Vision — <span className="text-primary">Digital Building Handoff</span></h2>
              <p className="lead">MEP Tools is evolving from a field tool into a full facility operating system. The end state is a building that increasingly manages itself.</p>
            </div>
          </div>

          <div className="row g-4 mb-5">
            <div className="col-lg-6" data-aos="fade-right">
              <h3 className="text-primary mb-3">The Problem</h3>
              <p>When a building is finished, the contractor hands the owner a binder — manuals, warranties, commissioning reports, as-built drawings. It goes in a drawer. The experienced tech who knew the building retires. Three years later, the facilities team is running reactive maintenance on a building designed to perform better.</p>
              <p className="mb-0">Facilities running reactive maintenance spend 40–50% more per square foot than those running structured preventive maintenance. For a $1M annual O&amp;M budget, that's $400K–$500K in avoidable costs — every year.</p>
            </div>
            <div className="col-lg-6" data-aos="fade-left">
              <h3 className="text-primary mb-3">The Solution</h3>
              <p>At construction closeout, the BIM model already contains every asset, specification, and O&amp;M document in a structured format called COBie. MEP Tools imports that data automatically — delivering a pre-loaded, live facility management system on Day 1 of occupancy instead of a PDF binder.</p>
              <ul className="list-unstyled mb-0">
                <li className="mb-2 d-flex"><i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i><span>Every asset pre-loaded — make, model, serial number, location</span></li>
                <li className="mb-2 d-flex"><i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i><span>O&amp;M manuals linked to every asset record</span></li>
                <li className="mb-2 d-flex"><i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i><span>PM schedule built and ready on Day 1</span></li>
                <li className="d-flex"><i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i><span>Compliance Calendar populated from equipment types</span></li>
              </ul>
            </div>
          </div>

          <div className="row g-4 mb-5">
            <div className="col-12 text-center mb-3">
              <h3 className="mb-1">The <span className="text-primary">Self-Healing Building</span> — Where This Goes</h3>
              <p className="lead text-muted">The seven-step cycle that transforms a building from passive to intelligent.</p>
            </div>
            {[
              { num: '1', title: 'COBie Import', text: 'Every asset, manual, and PM task loaded on Day 1 from BIM closeout data.' },
              { num: '2', title: 'Baseline Health Score', text: 'Facility Condition Index established from commissioning data.' },
              { num: '3', title: 'Scheduled Maintenance', text: 'PM Scheduler runs proactive work orders. Each completion updates the asset health score.' },
              { num: '4', title: 'Live BAS Monitoring', text: 'The building automation system feeds real-time sensor data into MEP Tools. Deviations from design spec are flagged automatically.' },
              { num: '5', title: 'Automatic Work Orders', text: 'Anomalies create work orders without human intervention, with O&M manuals already linked.' },
              { num: '6', title: 'Condition Update', text: 'Completing a work order updates the asset health score. PM intervals adjust based on actual performance.' },
              { num: '7', title: 'Predictive Intelligence', text: 'The system learns failure signatures and shifts from calendar-based to condition-based maintenance.' },
            ].map((step) => (
              <div className="col-md-6 col-lg-3" data-aos="fade-up" key={step.num}>
                <div className="card h-100 p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center me-3" style={{ width: '36px', height: '36px', flexShrink: 0, fontSize: '0.85rem', fontWeight: 700 }}>{step.num}</div>
                    <h6 className="mb-0">{step.title}</h6>
                  </div>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.9rem' }}>{step.text}</p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Roadmap */}
      <section className="section bg-eco-pattern">
        <div className="container">
          <div className="row text-center mb-5">
            <div className="col-lg-8 mx-auto">
              <h2 className="mb-3">What's <span className="text-primary">Coming Next</span></h2>
              <p className="lead">The platform is growing. Here's what's in active development after PM Scheduler.</p>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-md-6 col-lg-3" data-aos="fade-up">
              <div className="card h-100 p-4 text-center">
                <div className="feature-icon mx-auto mb-3">
                  <i className="bi bi-file-earmark-pdf"></i>
                </div>
                <h4>TAB Report Generator</h4>
                <p className="mb-0">Auto-generate AABC/NEBB-compliant test and balance reports from your DALT and Ebtron data. No Word templates, no copy-paste.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="100">
              <div className="card h-100 p-4 text-center">
                <div className="feature-icon mx-auto mb-3">
                  <i className="bi bi-diagram-3"></i>
                </div>
                <h4>COBie / BIM Import</h4>
                <p className="mb-0">Import COBie spreadsheet data from your project's BIM model and pre-load the facility asset inventory — turning a PDF binder into a live PM system at closeout.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="300">
              <div className="card h-100 p-4 text-center">
                <div className="feature-icon mx-auto mb-3">
                  <i className="bi bi-receipt"></i>
                </div>
                <h4>Utility Billing Tracker</h4>
                <p className="mb-0">Track monthly utility costs per facility, flag anomalies against rolling baselines, and feed real fuel cost data directly into the Boiler ECM calculator.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="section section-light">
        <div className="container">
          <div className="row text-center mb-5">
            <div className="col-lg-8 mx-auto">
              <h2 className="mb-3">Simple, <span className="text-primary">Predictable Pricing</span></h2>
              <p className="lead">MEP Tools uses a per-building subscription model — so you pay for the facilities you manage, not the number of users on your team.</p>
            </div>
          </div>

          <div className="row justify-content-center">
            <div className="col-lg-8" data-aos="fade-up">
              <div className="card p-4 p-md-5 shadow">
                <div className="row g-4 align-items-center">
                  <div className="col-md-6">
                    <h3 className="text-primary mb-3">Per-Building Subscription</h3>
                    <ul className="list-unstyled mb-4">
                      <li className="mb-3 d-flex">
                        <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                        <span>All current modules included</span>
                      </li>
                      <li className="mb-3 d-flex">
                        <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                        <span>Unlimited users per building</span>
                      </li>
                      <li className="mb-3 d-flex">
                        <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                        <span>Access to all new modules as they launch</span>
                      </li>
                      <li className="mb-3 d-flex">
                        <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                        <span>Multi-project and multi-building organization</span>
                      </li>
                      <li className="d-flex">
                        <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                        <span>Volume discounts for large portfolios</span>
                      </li>
                    </ul>
                    <Link to="/contact" className="btn btn-primary btn-lg">
                      Contact Us for Pricing <i className="bi bi-arrow-right ms-2"></i>
                    </Link>
                  </div>
                  <div className="col-md-6 text-center">
                    <div className="bg-primary bg-opacity-10 p-4 rounded-4 border border-primary">
                      <i className="bi bi-building-fill text-primary mb-3" style={{ fontSize: '3rem' }}></i>
                      <h4 className="text-primary">One price per building.</h4>
                      <p className="mb-0">No per-seat fees. No feature tiers. No surprises. The full platform, priced for the facility you're managing.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-primary text-white text-center">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="mb-4">Ready to see MEP Tools in action?</h2>
                <p className="lead mb-4">Contact us to schedule a demo or discuss how MEP Tools fits your team's workflow.</p>
                <div className="d-flex flex-wrap justify-content-center gap-3">
                  <Link to="/contact" className="btn btn-light btn-lg">
                    Request a Demo
                  </Link>
                  <Link to="/services" className="btn btn-outline-light btn-lg">
                    View Our Consulting Services
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default MEPTools;
